import React from 'react';
import styles from './index.less';
import { F } from '@/utils/intl';
import { Tooltip } from 'antd';

interface BookingSettingsVisualizationProps {
  duration: number;
  buffer: number;
  increment: number;
}

const BookingSettingsVisualization: React.FC<BookingSettingsVisualizationProps> = ({
  duration: rawDuration,
  buffer: rawBuffer,
  increment: rawIncrement,
}) => {
  const duration = Number(rawDuration) || 30;
  const buffer = Number(rawBuffer) || 0;
  const increment = Number(rawIncrement) || 15;

  const totalMinutes = React.useMemo(() => {
    if (duration <= 0 || increment <= 0) return 120;

    // First appointment: 0 to duration
    const t1End = duration;
    const t1BufferEnd = t1End + buffer;

    // Second appointment start: first increment >= t1BufferEnd
    const t2Start = Math.ceil(t1BufferEnd / increment) * increment;

    const t2End = t2Start + duration;
    const t2BufferEnd = t2End + buffer;

    // Show enough for at least 2 meetings + some padding
    const padding = Math.max(increment, 30);
    const needed = t2BufferEnd + padding;

    // Round up to a nice number (multiple of 30 or 60 if large)
    if (needed > 120) {
      return Math.ceil(needed / 60) * 60;
    }

    return 120;
  }, [duration, buffer, increment]);

  const scale = 100 / totalMinutes; // % per minute

  const formatTime = (mins: number) => {
    if (mins === 0) return '0';
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  const renderTimeline = () => {
    if (duration <= 0 || increment <= 0) return null;

    const slots = [];
    let currentTime = 0;
    let iterations = 0;
    const MAX_ITERATIONS = 200;

    while (currentTime + duration <= totalMinutes && iterations < 2) {
      iterations++;
      // Appointment block
      slots.push(
        <Tooltip key={`app-${currentTime}`} title={<F id="pages.locations.visualization.appointment" defaultMessage="Appointment: {duration} min" values={{ duration }} />}>
          <div
            className={styles.appointmentBlock}
            style={{
              left: `${currentTime * scale}%`,
              width: `${duration * scale}%`,
            }}
          >
            <span className={styles.blockLabel}>{formatTime(duration)}</span>
          </div>
        </Tooltip>
      );

      // Buffer block
      if (buffer > 0) {
        slots.push(
          <Tooltip key={`buf-${currentTime}`} title={<F id="pages.locations.visualization.buffer" defaultMessage="Buffer: {buffer} min" values={{ buffer }} />}>
            <div
              className={styles.bufferBlock}
              style={{
                left: `${(currentTime + duration) * scale}%`,
                width: `${buffer * scale}%`,
              }}
            />
          </Tooltip>
        );
      }

      // Calculate next start time based on increment and (duration + buffer)
      const earliestNextStart = currentTime + duration + buffer;
      let nextStart = currentTime + increment;
      while (nextStart < earliestNextStart && nextStart + duration <= totalMinutes) {
        nextStart += increment;
      }

      if (nextStart === currentTime) {
          // Prevent infinite loop if increment is 0 (though min is 1)
          break;
      }

      currentTime = nextStart;
    }

    return slots;
  };

  const renderIncrements = () => {
    if (increment <= 0) return null;

    const markers = [];
    const iterations = Math.min(totalMinutes / increment, 200);

    for (let i = 0; i <= totalMinutes; i += increment) {
      if (markers.length > 200) break; // Extra safety

      // Only show label if there is enough space or it's a major increment
      const showLabel = increment >= 15 || i % 60 === 0;

      markers.push(
        <div
          key={`inc-${i}`}
          className={styles.incrementMarker}
          style={{ left: `${i * scale}%` }}
        >
          {showLabel && <span className={styles.markerLabel}>{formatTime(i)}</span>}
        </div>
      );
    }
    return markers;
  };

  return (
    <div className={styles.visualizationWrapper}>
      <div className={styles.visualizationHeader}>
        <F id="pages.locations.visualization.title" defaultMessage="Booking Flow Visualization" />
      </div>

      <div className={styles.timelineContainer}>
        <div className={styles.timelineLabels}>
          <div className={styles.timelineLabel}>
            <F id="entity.appointment.plural" defaultMessage="Appointments" />
          </div>
          <div className={styles.timelineLabel}>
            <F id="pages.locations.visualization.increments" defaultMessage="Available Slots" />
          </div>
        </div>

        <div className={styles.timelineContent}>
          <div className={styles.appointmentRow}>
            {renderTimeline()}
          </div>
          <div className={styles.incrementRow}>
            {renderIncrements()}
          </div>
        </div>
      </div>

      <div className={styles.visualizationLegend}>
        <div className={styles.legendItem}>
          <div className={`${styles.legendColor} ${styles.appointmentBlock}`} />
          <span><F id="entity.appointment.singular" defaultMessage="Appointment" /></span>
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendColor} ${styles.bufferBlock}`} />
          <span><F id="pages.locations.appointment_buffer" defaultMessage="Buffer" /></span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendMarker} />
          <span><F id="pages.locations.visualization.slotStart" defaultMessage="Potential Start" /></span>
        </div>
      </div>
    </div>
  );
};

export default BookingSettingsVisualization;
